<template>
  <registry-page
    title-id="warehouse-balances-title"
    title="Остатки"
    description="Текущие количества и стоимость по складским движениям."
  >
    <template #metadata>
      <div class="warehouse-summary" aria-label="Итоги по остаткам">
        <span data-testid="warehouse-balance-summary-quantity">Количество: {{ summary.quantity || '0' }}</span>
        <span data-testid="warehouse-balance-summary-total">Сумма: {{ formatMoney(summary.total_cost || '0.00') }}</span>
      </div>
    </template>
    <template #actions>
      <q-btn flat no-caps icon="refresh" label="Обновить" :loading="loading" @click="loadBalances" />
    </template>

    <app-data-table
      data-testid="warehouse-balances-table"
      title="Остатки по складам"
      :count="visibleRows.length"
      :rows="visibleRows"
      :columns="columns"
      :pagination="pagination"
      :state="tableState"
      :state-title="stateTitle"
      :state-message="stateMessage"
      storage-key="warehouse-balances"
      row-key="id"
      wrap-cells
      @reset="resetFilters"
      @retry="loadAll"
      @row-click="openStockCard"
    >
      <template #filters>
        <filter-bar
          v-model="search"
          search-label="Поиск по коду, названию или складу"
          search-test-id="warehouse-balance-search"
          :has-filters="hasFilters"
          :applied-filters="appliedFilters"
          @search="applySearch"
          @remove="removeFilter"
          @reset="resetFilters"
        >
          <template #quick>
            <q-select
              :model-value="warehouseId"
              data-testid="warehouse-balance-warehouse-filter"
              dense outlined clearable emit-value map-options label="Склад"
              :options="warehouseOptions"
              @update:model-value="applyWarehouse"
            />
            <q-select
              :model-value="nomenclatureId"
              data-testid="warehouse-balance-nomenclature-filter"
              dense outlined clearable emit-value map-options label="Номенклатура"
              :options="nomenclatureOptions"
              @update:model-value="applyNomenclature"
            />
          </template>
          <template #advanced>
            <div class="q-gutter-md">
              <q-select
                :model-value="warehouseId"
                dense outlined clearable emit-value map-options label="Склад"
                :options="warehouseOptions"
                @update:model-value="applyWarehouse"
              />
              <q-select
                :model-value="nomenclatureId"
                dense outlined clearable emit-value map-options label="Номенклатура"
                :options="nomenclatureOptions"
                @update:model-value="applyNomenclature"
              />
            </div>
          </template>
        </filter-bar>
      </template>
      <template #actions>
        <q-btn
          flat no-caps icon="download" label="CSV"
          aria-label="Скачать CSV"
          data-testid="warehouse-balances-export"
          :disable="visibleRows.length === 0"
          @click="downloadCsv"
        />
      </template>
      <template #body-cell-quantity="props">
        <q-td :props="props">
          <div class="warehouse-quantity-cell">
            <span>{{ props.value }}</span>
            <status-badge
              v-if="props.row.low_stock"
              tone="warning"
              icon="warning_amber"
              label="Низкий остаток"
              data-testid="warehouse-low-stock-marker"
            />
          </div>
        </q-td>
      </template>
    </app-data-table>

    <q-expansion-item v-if="diagnostics.length" class="warehouse-diagnostics" icon="info_outline" label="Диагностика расчета">
      <q-list dense>
        <q-item v-for="item in diagnostics" :key="item.key"><q-item-section>{{ item.message }}</q-item-section></q-item>
      </q-list>
    </q-expansion-item>

    <q-dialog v-model="cardOpen" position="right">
      <q-card class="warehouse-stock-card">
        <q-card-section class="row items-start justify-between q-col-gutter-md">
          <div class="col">
            <h2 class="warehouse-stock-card__title" data-testid="warehouse-stock-card-title">{{ cardTitle }}</h2>
            <div class="text-caption text-grey-7">{{ selectedWarehouseName }}</div>
          </div>
          <q-btn flat round dense icon="close" aria-label="Закрыть карточку" v-close-popup />
        </q-card-section>
        <q-card-section class="row q-gutter-sm q-pt-none">
          <q-chip dense square data-testid="warehouse-stock-card-opening">Начало: {{ stockCard.opening_balance || '0' }}</q-chip>
          <q-chip dense square data-testid="warehouse-stock-card-closing">Конец: {{ stockCard.closing_balance || '0' }}</q-chip>
        </q-card-section>
        <q-card-section class="q-pa-none">
          <q-table
            v-accessible-scroll-region="{ label: 'Движения по складской позиции', selector: '.q-table__middle' }"
            data-testid="warehouse-stock-card-table"
            flat wrap-cells row-key="id"
            :rows="stockCardRows"
            :columns="stockCardTableColumns"
            :loading="cardLoading"
            :pagination="{ rowsPerPage: 20 }"
          >
            <template #body-cell-document="props">
              <q-td :props="props">
                <q-btn
                  flat dense no-caps color="primary"
                  :label="documentLabel(props.row)"
                  :disable="!props.row.document_id"
                  data-testid="warehouse-stock-card-document-link"
                  @click.stop="openDocument(props.row)"
                />
              </q-td>
            </template>
          </q-table>
        </q-card-section>
      </q-card>
    </q-dialog>
  </registry-page>
</template>

<script>
import { formatMoney } from '../../services/moneyFormat'
import {
  AppDataTable,
  FilterBar,
  RegistryPage,
  StatusBadge
} from '../../components/ui'
import {
  balanceColumns,
  createWarehouseBrowseAdapter,
  isWarehouseError,
  nomenclatureOption,
  normalizeBalanceRows,
  normalizeDiagnostics,
  normalizeMovementRows,
  normalizeNomenclatureRows,
  normalizeWarehouseRows,
  stockCardColumns,
  warehouseDocumentRoutes,
  warehouseError,
  warehouseOption
} from '../../services/warehouseBrowse'

export default {
  name: 'WarehouseBalances',
  components: { AppDataTable, FilterBar, RegistryPage, StatusBadge },
  data() {
    const query = this.$route.query
    return {
      adapter: null,
      rows: [],
      warehouses: [],
      nomenclature: [],
      summary: { quantity: '0', total_cost: '0.00' },
      diagnostics: [],
      loading: true,
      errorMessage: '',
      accessDenied: false,
      search: String(query.q || ''),
      warehouseId: query.warehouse || null,
      nomenclatureId: query.item || null,
      pagination: { page: 1, rowsPerPage: 20, rowsNumber: 0 },
      columns: balanceColumns,
      requestIds: {},
      handlers: [],
      cardOpen: false,
      cardLoading: false,
      selectedRow: null,
      stockCard: {},
      stockCardRows: [],
      stockCardTableColumns: stockCardColumns,
      mountedReady: false
    }
  },
  computed: {
    permissions() {
      return this.$store.state.permissions || {}
    },
    canView() {
      return this.permissions.open_stock === true || this.permissions.edit_stock === true
    },
    warehouseOptions() {
      return this.warehouses.filter((row) => row.is_active !== false).map(warehouseOption)
    },
    nomenclatureOptions() {
      return this.nomenclature.filter((row) => row.is_active !== false).map(nomenclatureOption)
    },
    visibleRows() {
      const query = this.search.trim().toLocaleLowerCase('ru')
      if (!query) return this.rows
      return this.rows.filter((row) => {
        const warehouse = row.warehouse && row.warehouse.name ? row.warehouse.name : row.warehouse_name
        return [row.code, row.name, row.unit_name, warehouse]
          .some((value) => String(value || '').toLocaleLowerCase('ru').includes(query))
      })
    },
    hasFilters() {
      return Boolean(this.search || this.warehouseId || this.nomenclatureId)
    },
    appliedFilters() {
      const filters = []
      if (this.search) filters.push({ key: 'search', label: `Поиск: ${this.search}` })
      if (this.warehouseId) filters.push({ key: 'warehouse', label: `Склад: ${this.optionLabel(this.warehouseOptions, this.warehouseId)}` })
      if (this.nomenclatureId) filters.push({ key: 'item', label: `Номенклатура: ${this.optionLabel(this.nomenclatureOptions, this.nomenclatureId)}` })
      return filters
    },
    tableState() {
      if (!this.canView || this.accessDenied) return 'access-denied'
      if (this.errorMessage) return 'error'
      if (this.loading && this.rows.length === 0) return 'loading'
      if (this.visibleRows.length === 0) return this.hasFilters ? 'filtered-empty' : 'empty'
      return 'ready'
    },
    stateTitle() {
      if (this.tableState === 'filtered-empty') return 'По выбранным условиям остатков нет'
      if (this.tableState === 'error') return 'Остатки не загрузились'
      return ''
    },
    stateMessage() {
      return this.errorMessage || (this.tableState === 'empty' ? 'Остатки появятся после первого проведенного складского движения.' : '')
    },
    cardTitle() {
      const row = this.selectedRow || {}
      return `Карточка движения: ${row.code ? `${row.code} — ` : ''}${row.name || 'Номенклатура'}`
    },
    selectedWarehouseName() {
      const row = this.selectedRow || {}
      return row.warehouse && row.warehouse.name ? row.warehouse.name : row.warehouse_name || row.warehouse_id || ''
    }
  },
  watch: {
    '$route.query': {
      deep: true,
      handler(query) {
        const previousServerKey = `${this.warehouseId || ''}|${this.nomenclatureId || ''}`
        this.search = String(query.q || '')
        this.warehouseId = query.warehouse || null
        this.nomenclatureId = query.item || null
        const nextServerKey = `${this.warehouseId || ''}|${this.nomenclatureId || ''}`
        if (this.mountedReady && previousServerKey !== nextServerKey) this.loadBalances()
      }
    }
  },
  mounted() {
    this.adapter = createWarehouseBrowseAdapter(this.connection)
    this.bind('onGetStockBalances', this.onBalances)
    this.bind('onGetStockCard', this.onStockCard)
    this.bind('onListWarehouses', this.onWarehouses)
    this.bind('onListNomenclature', this.onNomenclature)
    this.bind('accessDeniedErrorReceived', this.onAccessDenied)
    const ready = () => this.loadAll()
    this.handlers.push(['connectionReady', ready])
    this.mountedReady = true
    if (this.connection.isAuthenticated) ready()
    else this.emitter.on('connectionReady', ready)
  },
  beforeUnmount() {
    this.handlers.forEach(([name, handler]) => this.emitter.off(name, handler))
  },
  methods: {
    formatMoney,
    bind(name, handler) {
      this.handlers.push([name, handler])
      this.emitter.on(name, handler)
    },
    loadAll() {
      this.errorMessage = ''
      this.accessDenied = false
      this.requestIds.warehouses = this.adapter.warehouses(false)
      this.requestIds.nomenclature = this.adapter.nomenclature(false)
      this.loadBalances()
    },
    loadBalances() {
      if (!this.adapter) return
      this.loading = true
      this.errorMessage = ''
      this.requestIds.balances = this.adapter.balances({ warehouse_id: this.warehouseId, nomenclature_id: this.nomenclatureId })
    },
    onBalances(response) {
      if (response.request_id !== this.requestIds.balances) return
      this.loading = false
      if (isWarehouseError(response)) {
        this.accessDenied = response.error === 'access_denied'
        this.errorMessage = this.accessDenied ? '' : warehouseError(response, 'Не удалось загрузить остатки')
        return
      }
      this.rows = normalizeBalanceRows(response)
      this.summary = response.summary || { quantity: '0', total_cost: '0.00' }
      this.diagnostics = normalizeDiagnostics(response)
      this.pagination = { ...this.pagination, rowsNumber: this.rows.length }
    },
    onWarehouses(response) {
      if (response.request_id !== this.requestIds.warehouses) return
      if (!isWarehouseError(response)) this.warehouses = normalizeWarehouseRows(response)
    },
    onNomenclature(response) {
      if (response.request_id !== this.requestIds.nomenclature) return
      if (!isWarehouseError(response)) this.nomenclature = normalizeNomenclatureRows(response)
    },
    onAccessDenied(response) {
      if (![this.requestIds.balances, this.requestIds.card].includes(response.request_id)) return
      if (response.request_id === this.requestIds.card) this.cardLoading = false
      else this.loading = false
      this.accessDenied = true
      this.errorMessage = ''
    },
    pushQuery(changes) {
      const query = { ...this.$route.query, ...changes }
      Object.keys(query).forEach((key) => {
        if (query[key] === null || query[key] === undefined || query[key] === '') delete query[key]
      })
      if (JSON.stringify(query) !== JSON.stringify(this.$route.query)) this.$router.push({ query })
    },
    applySearch(value) { this.pushQuery({ q: value || null }) },
    applyWarehouse(value) { this.pushQuery({ warehouse: value || null }) },
    applyNomenclature(value) { this.pushQuery({ item: value || null }) },
    removeFilter(key) {
      if (key === 'search') this.applySearch('')
      if (key === 'warehouse') this.applyWarehouse(null)
      if (key === 'item') this.applyNomenclature(null)
    },
    resetFilters() { this.pushQuery({ q: null, warehouse: null, item: null }) },
    optionLabel(options, value) {
      const option = options.find((item) => String(item.value) === String(value))
      return option ? option.label : value
    },
    openStockCard(event, row) {
      if (!row || !row.nomenclature_id) return
      this.selectedRow = row
      this.stockCard = {}
      this.stockCardRows = []
      this.cardOpen = true
      this.cardLoading = true
      this.requestIds.card = this.adapter.stockCard({ warehouse_id: row.warehouse_id, nomenclature_id: row.nomenclature_id })
    },
    onStockCard(response) {
      if (response.request_id !== this.requestIds.card) return
      this.cardLoading = false
      if (isWarehouseError(response)) {
        this.errorMessage = warehouseError(response, 'Не удалось загрузить карточку движения')
        return
      }
      this.stockCard = response.card || {}
      this.stockCardRows = normalizeMovementRows({ movements: this.stockCard.movements || [] })
    },
    documentLabel(row) {
      const document = row.document || {}
      return document.display_name || (row.document_id ? `Документ ${row.document_id}` : 'Без документа')
    },
    openDocument(row) {
      const document = row.document || {}
      const documentId = row.document_id || document.id
      if (!documentId) return
      this.$router.push({ name: warehouseDocumentRoutes[document.document_type] || 'document_info_upd', params: { id: documentId, history_id: 'last' } })
    },
    csvEscape(value) {
      return `"${String(value === undefined || value === null ? '' : value).replace(/"/g, '""')}"`
    },
    downloadCsv() {
      const header = this.columns.map((column) => this.csvEscape(column.label)).join(';')
      const body = this.visibleRows.map((row) => this.columns.map((column) => {
        const value = typeof column.field === 'function' ? column.field(row) : row[column.field || column.name]
        return this.csvEscape(value)
      }).join(';')).join('\n')
      const blob = new Blob([`${header}\n${body}`], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `warehouse-balances-${new Date().toISOString().slice(0, 10)}.csv`
      link.click()
      URL.revokeObjectURL(url)
    }
  }
}
</script>

<style scoped>
.warehouse-summary { display: flex; flex-wrap: wrap; gap: var(--sid-space-3); color: var(--sid-color-text-secondary); font-variant-numeric: tabular-nums; }
.warehouse-quantity-cell { display: inline-flex; min-width: 100%; align-items: center; justify-content: flex-end; gap: var(--sid-space-2); }
.warehouse-diagnostics { max-width: 56rem; margin-top: var(--sid-space-4); }
.warehouse-stock-card { width: min(48rem, 100vw); max-width: 100vw; min-height: 100vh; }
.warehouse-stock-card__title { margin: 0; font-size: var(--sid-font-size-section); line-height: var(--sid-line-height-section); }
:deep(.app-data-table) { min-width: 0; }
:deep(.filter-bar__quick > *) { min-width: 12rem; max-width: 18rem; }
:deep(.q-table__middle) { max-width: 100%; overflow: auto; }
@media (max-width: 599px) {
  .warehouse-summary { flex-direction: column; gap: var(--sid-space-1); }
}
</style>
